import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Smile, Cloud, CloudRain, AlertTriangle, Heart, Star, Music, Eye, Hand, Wind, Bookmark, BookmarkCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

export const ChildRegulationTools = () => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [bookmarks, setBookmarks] = useState({});
  const { token, API } = useAuth();

  const calmingTools = [
    {
      id: "bubble-breathing",
      title: "Bubble Breathing",
      icon: Wind,
      description: "Pretend you're blowing bubbles - slow and gentle",
      instructions: [
        "Take a deep breath in through your nose (smell the flowers)",
        "Blow out slowly through your mouth (blow the bubbles)",
        "Try to make the bubble as big as you can by blowing slowly",
        "Do this 5 times together"
      ],
      tip: "You can use real bubbles to make this more fun!"
    },
    {
      id: "star-stretch",
      title: "Star Stretch",
      icon: Star,
      description: "Reach out like a starfish and relax",
      instructions: [
        "Stand with your feet apart and arms out wide",
        "Stretch your fingers and toes as far as they can go",
        "Take a big breath and make yourself as BIG as a star",
        "Now slowly bring everything in and make yourself as small as a ball",
        "Repeat: big star, small ball, 5 times"
      ],
      tip: "Count to 5 while big, count to 5 while small"
    },
    {
      id: "color-hunt",
      title: "Color Hunt",
      icon: Eye,
      description: "Find things around you by color",
      instructions: [
        "Pick a color together (start with blue)",
        "Look around and find 5 things that are that color",
        "Point to each one and name it",
        "Pick another color and do it again",
        "This helps bring attention to the present moment"
      ],
      tip: "Make it a game - who can find 5 things first?"
    },
    {
      id: "gentle-touch",
      title: "Gentle Touch",
      icon: Hand,
      description: "Soft touches to calm the nervous system",
      instructions: [
        "With your child's permission, gently stroke their arm",
        "Use slow, downward strokes from shoulder to hand",
        "Or let them stroke a soft blanket or stuffed animal",
        "Focus on the soft feeling together",
        "Take slow breaths while doing this"
      ],
      tip: "Ask first - some children prefer not to be touched when upset"
    }
  ];

  const roughDayTools = [
    {
      id: "bear-hug",
      title: "Bear Hug",
      icon: Heart,
      description: "Deep pressure to help feel grounded",
      instructions: [
        "Ask if your child wants a big bear hug",
        "Wrap your arms around them firmly (but not too tight)",
        "Hold for 20-30 seconds while taking deep breaths together",
        "Rock gently side to side if that feels good",
        "The pressure can help calm an overwhelmed nervous system"
      ],
      tip: "If they don't want a hug, they can squeeze a pillow tightly instead"
    },
    {
      id: "shake-it-out",
      title: "Shake It Out",
      icon: Music,
      description: "Movement to release built-up energy",
      instructions: [
        "Put on a favorite song (or just count together)",
        "Shake your hands really fast for 30 seconds",
        "Shake your feet and legs",
        "Shake your whole body like a wet dog",
        "Then freeze like a statue - notice how different your body feels"
      ],
      tip: "This is especially good for anxiety or restless feelings"
    },
    {
      id: "ice-cube-hold",
      title: "Ice Cube Hold",
      icon: Cloud,
      description: "Strong sensation to redirect focus",
      instructions: [
        "Get an ice cube from the freezer",
        "Hold it in your hands together",
        "Focus on how cold it feels",
        "See how long you can hold it before it melts",
        "The strong sensation can help interrupt spiraling thoughts"
      ],
      tip: "Have a towel ready! This works well for intense emotions"
    },
    {
      id: "wall-push",
      title: "Wall Push",
      icon: Hand,
      description: "Heavy work for the muscles",
      instructions: [
        "Stand facing a wall, arms out",
        "Push against the wall as hard as you can",
        "Count to 10 together while pushing",
        "Rest for 5 seconds",
        "Repeat 3-5 times"
      ],
      tip: "Heavy muscle work is very calming for the nervous system"
    }
  ];

  const extremeDayTools = [
    {
      id: "safe-space",
      title: "Safe Space Retreat",
      icon: Cloud,
      description: "Create a calm-down corner",
      instructions: [
        "Go to a pre-planned safe space (under a table with blankets, a closet, a tent)",
        "Keep it dim and quiet",
        "Have comfort items ready: weighted blanket, stuffed animal, fidget",
        "Stay nearby but give space",
        "Don't try to talk or reason - just be present"
      ],
      tip: "Set this space up BEFORE a crisis so it's ready when needed"
    },
    {
      id: "co-regulation",
      title: "Co-Regulation Breathing",
      icon: Wind,
      description: "Your calm helps them calm",
      instructions: [
        "Sit or stand near your child (at their level if possible)",
        "Don't ask them to breathe - just do it yourself, loudly",
        "Take slow, exaggerated breaths they can hear",
        "Your nervous system helps regulate theirs",
        "This may take several minutes - that's okay"
      ],
      tip: "Focus on staying calm yourself. You can't regulate them if you're dysregulated"
    },
    {
      id: "counting-anchor",
      title: "Counting Anchor",
      icon: Eye,
      description: "Simple repetition to ground",
      instructions: [
        "In a calm voice, start counting slowly: 1... 2... 3...",
        "Keep going up to 10, then start over",
        "The rhythm and predictability can be soothing",
        "You can also count objects in the room",
        "Don't expect them to count with you - just keep going"
      ],
      tip: "Your steady voice is an anchor even if they seem not to hear"
    },
    {
      id: "reduce-everything",
      title: "Reduce Everything",
      icon: AlertTriangle,
      description: "Minimize sensory input",
      instructions: [
        "Turn off all screens and unnecessary lights",
        "Reduce noise as much as possible",
        "Speak in short, simple phrases or stay quiet",
        "Remove extra people from the space",
        "Wait. Time and reduced input are your friends"
      ],
      tip: "Less is more during extreme moments. Don't add stimulation"
    }
  ];

  // Fetch bookmarks on mount
  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks?tool_type=child-tool`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bookmarkMap = {};
      response.data.forEach(b => {
        bookmarkMap[b.tool_id] = true;
      });
      setBookmarks(bookmarkMap);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const toggleBookmark = async (e, toolId, toolTitle, category) => {
    e.stopPropagation(); // Prevent card expansion
    try {
      if (bookmarks[toolId]) {
        await axios.delete(`${API}/bookmarks/tool/child-tool/${toolId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookmarks(prev => ({ ...prev, [toolId]: false }));
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/bookmarks`, {
          tool_type: 'child-tool',
          tool_id: toolId,
          tool_title: toolTitle,
          tool_category: category
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookmarks(prev => ({ ...prev, [toolId]: true }));
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const renderToolCard = (tool, intensity) => {
    const Icon = tool.icon;
    const colorClasses = {
      calming: 'border-l-green-400 bg-green-50',
      rough: 'border-l-amber-400 bg-amber-50',
      extreme: 'border-l-red-300 bg-red-50'
    };

    return (
      <Card 
        key={tool.id}
        className={`bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden cursor-pointer transition-all hover:shadow-md ${
          selectedTool === tool.id ? 'ring-2 ring-primary' : ''
        }`}
        onClick={() => setSelectedTool(selectedTool === tool.id ? null : tool.id)}
        data-testid={`tool-${tool.id}`}
      >
        <div className={`border-l-4 ${colorClasses[intensity]} p-6`}>
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-white shadow-sm">
              <Icon className="w-6 h-6 text-[#57534E]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">{tool.title}</h3>
                <button
                  onClick={(e) => toggleBookmark(e, tool.id, tool.title, intensity)}
                  className="p-1 hover:bg-white/50 rounded-full transition-colors"
                  data-testid={`bookmark-${tool.id}`}
                >
                  {bookmarks[tool.id] ? (
                    <BookmarkCheck className="w-5 h-5 text-primary fill-primary" />
                  ) : (
                    <Bookmark className="w-5 h-5 text-[#A8A29E] hover:text-primary" />
                  )}
                </button>
              </div>
              <p className="text-[#57534E] text-sm">{tool.description}</p>
            </div>
          </div>
          
          {selectedTool === tool.id && (
            <div className="mt-6 space-y-4">
              <div className="bg-white rounded-lg p-4 border border-stone-200">
                <h4 className="font-semibold text-[#2D2D2D] mb-3">How to do it:</h4>
                <ol className="space-y-2">
                  {tool.instructions.map((instruction, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#57534E]">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">
                        {i + 1}
                      </span>
                      <span>{instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="bg-secondary/20 rounded-lg p-4">
                <p className="text-sm">
                  <span className="font-semibold text-secondary-foreground">Tip: </span>
                  <span className="text-[#57534E]">{tool.tip}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-8" data-testid="child-regulation-tools">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <Smile className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            Child Regulation Tools
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          Age-appropriate co-regulation strategies organized by intensity level. Choose tools based on how your child is feeling right now.
        </p>
      </div>

      {/* Intensity Guide */}
      <Card className="bg-white rounded-xl border border-stone-100 p-6">
        <h2 className="font-nunito font-bold text-[#2D2D2D] mb-4">Choose the Right Tools</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
            <Cloud className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-800">Calming Days</h3>
              <p className="text-sm text-green-700">Mild stress, needs gentle grounding</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
            <CloudRain className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800">Rough Days</h3>
              <p className="text-sm text-amber-700">Elevated stress, needs active intervention</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-800">Extreme Days</h3>
              <p className="text-sm text-red-700">Crisis mode, focus on safety and calm</p>
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="calming" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-white rounded-xl border border-stone-200 p-2">
          <TabsTrigger 
            value="calming" 
            className="rounded-lg data-[state=active]:bg-green-500 data-[state=active]:text-white"
            data-testid="tab-calming"
          >
            <Cloud className="w-4 h-4 mr-2" />Calming
          </TabsTrigger>
          <TabsTrigger 
            value="rough" 
            className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white"
            data-testid="tab-rough"
          >
            <CloudRain className="w-4 h-4 mr-2" />Rough Days
          </TabsTrigger>
          <TabsTrigger 
            value="extreme" 
            className="rounded-lg data-[state=active]:bg-red-400 data-[state=active]:text-white"
            data-testid="tab-extreme"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />Extreme
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calming" className="space-y-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-green-800">
              <span className="font-semibold">For calming days:</span> Your child might be slightly anxious, restless, or need help transitioning. These gentle tools help maintain regulation.
            </p>
          </div>
          {calmingTools.map(tool => renderToolCard(tool, 'calming'))}
        </TabsContent>

        <TabsContent value="rough" className="space-y-4">
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="text-amber-800">
              <span className="font-semibold">For rough days:</span> Your child is having a hard time - maybe irritable, tearful, or struggling to cope. These tools provide more active regulation support.
            </p>
          </div>
          {roughDayTools.map(tool => renderToolCard(tool, 'rough'))}
        </TabsContent>

        <TabsContent value="extreme" className="space-y-4">
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-red-800">
              <span className="font-semibold">For extreme days:</span> Your child is in crisis - meltdown, rage, or complete dysregulation. Focus on safety, reduce stimulation, and co-regulate. These moments will pass.
            </p>
          </div>
          <Card className="bg-white rounded-xl border-2 border-red-200 p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div>
                <h3 className="font-nunito font-bold text-[#2D2D2D] mb-2">Safety First</h3>
                <ul className="space-y-2 text-[#57534E]">
                  <li>- Remove dangerous objects from reach</li>
                  <li>- Protect siblings and move them to another room if needed</li>
                  <li>- Don't try to reason or lecture during the crisis</li>
                  <li>- Your job is to stay calm and wait it out safely</li>
                  <li>- This is the illness, not your child</li>
                </ul>
              </div>
            </div>
          </Card>
          {extremeDayTools.map(tool => renderToolCard(tool, 'extreme'))}
        </TabsContent>
      </Tabs>

      {/* Bottom Reminder */}
      <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-stone-100 p-6">
        <div className="flex items-start gap-4">
          <Heart className="w-8 h-8 text-primary flex-shrink-0" />
          <div>
            <h3 className="font-nunito font-bold text-[#2D2D2D] mb-2">Remember</h3>
            <p className="text-[#57534E] leading-relaxed">
              Every child is different, and what works one day might not work the next. The goal is not to stop all difficult moments, but to help your child (and you) get through them safely. You are doing an amazing job by being here and learning these tools.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChildRegulationTools;
